import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Chip,
  Divider,
  Button,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Warning as WarningIcon,
  Description as DocIcon,
  Refresh as RedoIcon,
  ThumbUp as ConfirmIcon,
  SwapHoriz as OverrideIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

interface ValidationDetailDrawerProps {
  check: any;
  open: boolean;
  onClose: () => void;
  onCitationClick: (citation: any) => void;
  onRerun: (checkId: string) => void;
  onReviewUpdate: (checkId: string, status: string, note?: string) => void;
  rerunning?: boolean;
}

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; label: string; color: string; bg: string }> = {
  PASS: {
    icon: <PassIcon sx={{ fontSize: 22 }} />,
    label: "Pass",
    color: tokens.status.success,
    bg: `${tokens.status.success}10`,
  },
  FAIL: {
    icon: <FailIcon sx={{ fontSize: 22 }} />,
    label: "Fail",
    color: tokens.status.danger,
    bg: `${tokens.status.danger}10`,
  },
  WARNING: {
    icon: <WarningIcon sx={{ fontSize: 22 }} />,
    label: "Warning",
    color: tokens.status.warning,
    bg: `${tokens.status.warning}10`,
  },
};

export const ValidationDetailDrawer: React.FC<ValidationDetailDrawerProps> = ({
  check,
  open,
  onClose,
  onCitationClick,
  onRerun,
  onReviewUpdate,
  rerunning,
}) => {
  const [noteText, setNoteText] = useState(check?.reviewNote || "");

  useEffect(() => {
    setNoteText(check?.reviewNote || "");
  }, [check?.checkId, check?.reviewNote]);

  if (!check) return null;

  const statusCfg = STATUS_CONFIG[check.status] || STATUS_CONFIG.WARNING;
  const isReviewed = check.reviewStatus === "CONFIRMED" || check.reviewStatus === "OVERRIDDEN";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          borderRadius: 0,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${tokens.border.light}`,
          bgcolor: statusCfg.bg,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ color: statusCfg.color }}>{statusCfg.icon}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: tokens.fontSize.base,
                  fontWeight: tokens.fontWeight.heavy,
                  color: tokens.text.primary,
                }}
              >
                {check.checkName}
              </Typography>
              <Stack direction="row" spacing={1} mt={0.5}>
                <Chip
                  label={statusCfg.label}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: tokens.fontSize["2xs"],
                    fontWeight: tokens.fontWeight.semibold,
                    bgcolor: statusCfg.color,
                    color: "#fff",
                  }}
                />
                {check.category && (
                  <Chip
                    label={check.category}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: tokens.fontSize["2xs"] }}
                  />
                )}
                {isReviewed && (
                  <Chip
                    label={check.reviewStatus === "CONFIRMED" ? "Confirmed" : "Overridden"}
                    size="small"
                    color={check.reviewStatus === "CONFIRMED" ? "success" : "warning"}
                    variant="outlined"
                    sx={{ height: 22 }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2.5 }}>
        {/* Summary */}
        {check.detail && (
          <Box mb={3}>
            <Typography
              variant="overline"
              sx={{
                fontSize: tokens.fontSize["2xs"],
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.muted,
                letterSpacing: "0.08em",
              }}
            >
              Summary
            </Typography>
            <Typography
              sx={{
                fontSize: tokens.fontSize.sm2,
                color: tokens.text.primary,
                mt: 0.5,
                lineHeight: 1.6,
              }}
            >
              {check.detail}
            </Typography>
          </Box>
        )}

        {/* Detailed Analysis */}
        {check.reasoning && (
          <Box mb={3}>
            <Typography
              variant="overline"
              sx={{
                fontSize: tokens.fontSize["2xs"],
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.muted,
                letterSpacing: "0.08em",
              }}
            >
              Detailed Analysis
            </Typography>
            <Box
              sx={{
                mt: 0.75,
                p: 2,
                bgcolor: "#f8f9fa",
                borderRadius: tokens.radius.md,
                borderLeft: `3px solid ${statusCfg.color}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: tokens.fontSize.sm2,
                  color: tokens.text.body,
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.7,
                }}
              >
                {check.reasoning}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 2.5 }} />

        {/* Evidence / Citations */}
        {check.citations?.length > 0 && (
          <Box mb={3}>
            <Typography
              variant="overline"
              sx={{
                fontSize: tokens.fontSize["2xs"],
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.muted,
                letterSpacing: "0.08em",
              }}
            >
              Evidence ({check.citations.length} citation{check.citations.length !== 1 ? "s" : ""})
            </Typography>
            <Stack spacing={1} mt={1}>
              {check.citations.map((citation: any, idx: number) => (
                <Box
                  key={idx}
                  onClick={() => onCitationClick(citation)}
                  sx={{
                    p: 1.5,
                    borderRadius: tokens.radius.sm,
                    border: `1px solid ${tokens.border.light}`,
                    cursor: citation.documentId ? "pointer" : "default",
                    transition: "all 150ms ease",
                    "&:hover": citation.documentId
                      ? {
                          borderColor: tokens.brand.primary,
                          bgcolor: `${tokens.brand.primary}04`,
                        }
                      : {},
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <DocIcon
                      sx={{
                        fontSize: 16,
                        color: tokens.text.muted,
                        mt: 0.25,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          sx={{
                            fontSize: tokens.fontSize.sm2,
                            fontWeight: tokens.fontWeight.semibold,
                            color: citation.documentId ? tokens.brand.primary : tokens.text.primary,
                          }}
                        >
                          {citation.documentName}
                        </Typography>
                        {citation.pageNumber && (
                          <Chip
                            label={`p. ${citation.pageNumber}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "10px" }}
                          />
                        )}
                      </Stack>
                      {citation.excerpt && (
                        <Typography
                          sx={{
                            fontSize: tokens.fontSize.xs,
                            color: tokens.text.muted,
                            fontStyle: "italic",
                            mt: 0.5,
                            lineHeight: 1.5,
                          }}
                        >
                          &ldquo;{citation.excerpt}&rdquo;
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Linked Documents (for cross-doc checks) */}
        {check.linkedDocuments?.length > 0 && (
          <Box mb={3}>
            <Typography
              variant="overline"
              sx={{
                fontSize: tokens.fontSize["2xs"],
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.muted,
                letterSpacing: "0.08em",
              }}
            >
              Documents Compared
            </Typography>
            <Stack direction="row" spacing={0.75} mt={0.75} flexWrap="wrap" useFlexGap>
              {check.linkedDocuments.map((ld: any, idx: number) => (
                <Chip
                  key={idx}
                  icon={<DocIcon sx={{ fontSize: 14 }} />}
                  label={ld.documentName}
                  size="small"
                  variant="outlined"
                  sx={{ height: 26, fontSize: tokens.fontSize.xs }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Annotation / Review Note */}
        <Box mb={3}>
          <Typography
            variant="overline"
            sx={{
              fontSize: tokens.fontSize["2xs"],
              fontWeight: tokens.fontWeight.semibold,
              color: tokens.text.muted,
              letterSpacing: "0.08em",
            }}
          >
            Annotation
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add context, justification for override, or notes for the memo..."
            size="small"
            sx={{
              mt: 0.5,
              "& .MuiOutlinedInput-root": {
                fontSize: tokens.fontSize.sm2,
                borderRadius: tokens.radius.md,
              },
            }}
          />
        </Box>
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${tokens.border.light}`,
          bgcolor: tokens.bg.surface,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            size="small"
            variant="outlined"
            startIcon={<RedoIcon sx={{ fontSize: 16 }} />}
            onClick={() => onRerun(check.checkId)}
            disabled={rerunning}
            sx={{
              textTransform: "none",
              fontSize: tokens.fontSize.sm2,
              fontWeight: tokens.fontWeight.semibold,
              color: tokens.text.primary,
              borderColor: tokens.border.default,
              borderRadius: tokens.radius.sm,
            }}
          >
            Re-run Check
          </Button>

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant={check.reviewStatus === "CONFIRMED" ? "contained" : "outlined"}
              color="success"
              startIcon={<ConfirmIcon sx={{ fontSize: 14 }} />}
              onClick={() => onReviewUpdate(check.checkId, "CONFIRMED", noteText || undefined)}
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              Confirm
            </Button>
            <Button
              size="small"
              variant={check.reviewStatus === "OVERRIDDEN" ? "contained" : "outlined"}
              color="warning"
              startIcon={<OverrideIcon sx={{ fontSize: 14 }} />}
              onClick={() => onReviewUpdate(check.checkId, "OVERRIDDEN", noteText || undefined)}
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              Override
            </Button>
            {isReviewed && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  setNoteText("");
                  onReviewUpdate(check.checkId, "PENDING", "");
                }}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                Clear
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
};
